<template>
  <div class="page-body">
    <div class="container-xl">
      <div class="page-header d-print-none mb-3">
        <div class="row align-items-center">
          <div class="col">
            <div class="page-pretitle">access control</div>
            <h2 class="page-title">Users</h2>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body border-bottom py-3">
          <div class="row g-2 align-items-center">
            <div class="col-12 col-md">
              <label class="form-label visually-hidden" for="user-search">Search users</label>
              <input id="user-search" v-model="searchInput" type="search" class="form-control"
                placeholder="Search name, email or role" autocomplete="off" />
            </div>
            <div class="col-12 col-md-auto">
              <label class="form-label visually-hidden" for="role-filter">Filter by role</label>
              <select id="role-filter" class="form-select" :value="roleFilter"
                @change="updateQuery({ role: $event.target.value, page: 1 })">
                <option value="">All roles</option>
                <option v-for="role of roles" :key="role.id" :value="role.role">{{ role.role }}</option>
              </select>
            </div>
            <div class="col-12 col-md-auto text-secondary small" aria-live="polite">{{ countLabel }}</div>
          </div>
        </div>

        <table class="table card-table users-table">
          <thead>
            <tr>
              <th scope="col">
                <button class="th-sort" @click="toggleSort('email')">
                  User <span class="text-secondary">{{ sortIndicator('email') }}</span>
                </button>
              </th>
              <th scope="col">Roles</th>
              <th scope="col">
                <button class="th-sort" @click="toggleSort('created_at')">
                  Created <span class="text-secondary">{{ sortIndicator('created_at') }}</span>
                </button>
              </th>
              <th scope="col" class="w-1"><span class="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user of users" :key="user.id">
              <td class="user-cell" @click="detailModalUser = user">
                <div class="d-flex align-items-center">
                  <span class="avatar avatar-sm me-2" :style="avatarStyle(user)">
                    <template v-if="!user.has_image">{{ initials(user) }}</template>
                  </span>
                  <div class="text-truncate">
                    <div v-if="user.name">{{ user.name }}</div>
                    <div :class="user.name ? 'text-secondary small' : ''">{{ user.email }}</div>
                  </div>
                </div>
              </td>
              <td class="roles-cell" @click="openRoles(user)">
                <span v-for="role of visibleRoles(user)" :key="role.id" class="badge role-badge me-1"
                  :class="isPrivileged(role) ? 'bg-red-lt' : 'bg-blue-lt'">{{ role.role }}</span>
                <span v-if="hiddenRoleCount(user)" class="badge bg-secondary-lt"
                  :title="hiddenRoleNames(user)">+{{ hiddenRoleCount(user) }}</span>
                <span v-if="!user.roles.length" class="text-secondary">&mdash;</span>
              </td>
              <td class="text-secondary created-cell" :title="user.created_at">{{ formatDate(user.created_at) }}</td>
              <td class="text-end actions-cell">
                <div class="dropdown">
                  <button class="btn btn-ghost-secondary btn-icon" data-bs-toggle="dropdown" aria-label="User actions">
                    <svg xmlns="http://www.w3.org/2000/svg" class="icon" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="19" r="1"></circle><circle cx="12" cy="5" r="1"></circle></svg>
                  </button>
                  <div class="dropdown-menu dropdown-menu-end">
                    <button class="dropdown-item" @click="openRoles(user)">Edit roles&hellip;</button>
                    <button class="dropdown-item" @click="detailModalUser = user">View details</button>
                    <div class="dropdown-divider"></div>
                    <button class="dropdown-item text-danger" :disabled="isCurrentUser(user)"
                      :title="isCurrentUser(user) ? 'You cannot delete your own account' : ''"
                      @click="confirmDelete(user)">Delete user</button>
                  </div>
                </div>
              </td>
            </tr>
            <tr v-if="loading && !users.length" class="state-row">
              <td colspan="4" class="text-secondary text-center py-4">
                <span class="spinner-border spinner-border-sm me-2"></span>Loading users&hellip;
              </td>
            </tr>
            <tr v-else-if="!loading && !users.length" class="state-row">
              <td colspan="4" class="text-secondary text-center py-4">
                {{ search || roleFilter ? 'No users match your filters.' : 'No users yet.' }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="card-footer d-flex align-items-center flex-wrap gap-2">
          <span class="text-secondary small">{{ rangeLabel }}</span>
          <div class="ms-auto d-flex align-items-center gap-2">
            <label class="form-label visually-hidden" for="per-page">Rows per page</label>
            <select id="per-page" class="form-select form-select-sm w-auto" :value="perPage"
              @change="updateQuery({ perPage: $event.target.value, page: 1 })">
              <option v-for="size of PER_PAGE_OPTIONS" :key="size" :value="size">{{ size }}</option>
            </select>
            <ul class="pagination m-0">
              <li class="page-item" :class="page === 1 ? 'disabled' : ''">
                <button class="page-link" :disabled="page === 1" aria-label="Previous page" @click="page = page - 1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><polyline points="15 6 9 12 15 18"></polyline></svg>
                </button>
              </li>
              <li v-for="(pageItem, index) of pageWindow" :key="index" class="page-item"
                :class="pageItem === '…' ? 'disabled' : (page === pageItem ? 'active' : '')">
                <span v-if="pageItem === '…'" class="page-link">&hellip;</span>
                <button v-else class="page-link" @click="page = pageItem">{{ pageItem }}</button>
              </li>
              <li class="page-item" :class="page === pagesCount ? 'disabled' : ''">
                <button class="page-link" :disabled="page === pagesCount" aria-label="Next page" @click="page = page + 1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="icon" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><polyline points="9 6 15 12 9 18"></polyline></svg>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <UserRolesModal :user="rolesModalUser" :roles="roles" @close="rolesModalUser = null" @saved="onRolesSaved" />
    <UserDetailModal v-if="!rolesModalUser" :user="detailModalUser" @close="detailModalUser = null"
      @edit-roles="openRolesFromDetail" />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { api } from '@/api'
import UserRolesModal from '@/components/Admin/UserRolesModal.vue'
import UserDetailModal from '@/components/Admin/UserDetailModal.vue'

const $vue = getCurrentInstance()
const route = useRoute()
const router = useRouter()
const store = useStore()

const PER_PAGE_OPTIONS = [10, 25, 50, 100]
const PRIVILEGED_ROLES = ['SUPER_ADMIN']
const VISIBLE_ROLE_CHIPS = 2
const SEARCH_DEBOUNCE_MS = 300

const users = ref([])
const roles = ref([])
const total = ref(0)
const loading = ref(false)
const rolesModalUser = ref(null)
const detailModalUser = ref(null)
const searchInput = ref(route.query.search || '')

function updateQuery (patch) {
  router.replace({ ...route, query: { ...route.query, ...patch } })
}

const search = computed(() => route.query.search || '')
const roleFilter = computed(() => route.query.role || '')
const sort = computed(() => route.query.sort || 'email')
const dir = computed(() => route.query.dir || 'asc')
const perPage = computed(() => Number(route.query.perPage) || 25)
const pagesCount = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)))
const page = computed({
  get: () => Math.max(1, Number(route.query.page) || 1),
  set (page) {
    if (page < 1 || page > pagesCount.value) return
    updateQuery({ page })
  }
})

const countLabel = computed(() => {
  if (loading.value && !users.value.length) return 'Loading…'
  const suffix = search.value || roleFilter.value ? ' matching' : ''
  return total.value === 1 ? '1 user' + suffix : total.value + ' users' + suffix
})
const rangeLabel = computed(() => {
  if (!total.value) return ''
  const first = (page.value - 1) * perPage.value + 1
  return 'Showing ' + first + '–' + Math.min(first + users.value.length - 1, total.value) + ' of ' + total.value
})
const pageWindow = computed(() => {
  const totalPages = pagesCount.value
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1)
  const first = Math.max(2, page.value - 1)
  const last = Math.min(totalPages - 1, page.value + 1)
  const window = [1]
  if (first > 2) window.push('…')
  for (let pageItem = first; pageItem <= last; pageItem++) window.push(pageItem)
  if (last < totalPages - 1) window.push('…')
  window.push(totalPages)
  return window
})

function isPrivileged (role) {
  return PRIVILEGED_ROLES.includes(role.role)
}

// Privileged roles sort first so they are never hidden behind the "+N" chip.
function sortedRoles (user) {
  return [...(user.roles || [])].sort((a, b) => Number(isPrivileged(b)) - Number(isPrivileged(a)))
}

function visibleRoles (user) {
  return sortedRoles(user).slice(0, VISIBLE_ROLE_CHIPS)
}

function hiddenRoleCount (user) {
  return Math.max(0, (user.roles || []).length - VISIBLE_ROLE_CHIPS)
}

function hiddenRoleNames (user) {
  return sortedRoles(user).slice(VISIBLE_ROLE_CHIPS).map(role => role.role).join(', ')
}

function initials (user) {
  const source = user.name || user.email
  return source.split(/[\s@._+-]+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('')
}

function avatarStyle (user) {
  if (!user.has_image) return {}
  return { backgroundImage: 'url(' + api.defaults.baseURL + '/admin/users/' + user.id + '/image)' }
}

function formatDate (value) {
  if (!value) return ''
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function sortIndicator (key) {
  if (sort.value !== key) return ''
  return dir.value === 'asc' ? '↑' : '↓'
}

function toggleSort (key) {
  if (sort.value === key) {
    updateQuery({ dir: dir.value === 'asc' ? 'desc' : 'asc', page: 1 })
  } else {
    updateQuery({ sort: key, dir: 'asc', page: 1 })
  }
}

function isCurrentUser (user) {
  return store.state.user?.Email?.toLowerCase() === user.email.toLowerCase()
}

function notifyError (err, fallback) {
  $vue?.proxy.$swal.fire({ icon: 'error', text: err?.response?.data?.Data || fallback })
}

let latestRequestId = 0

async function loadUsers () {
  const requestId = ++latestRequestId
  loading.value = true
  try {
    const response = await store.dispatch('getUsersPaged', {
      search: search.value || undefined,
      role: roleFilter.value || undefined,
      sort: sort.value,
      dir: dir.value,
      page: page.value,
      perPage: perPage.value
    })
    // A newer request started while this one was in flight; its result wins.
    if (requestId !== latestRequestId) return
    users.value = response.Data.items || []
    total.value = response.Data.total || 0
    // A filter change can strand us past the last page; step back onto it.
    if (!users.value.length && total.value > 0 && page.value > 1) {
      updateQuery({ page: pagesCount.value })
    }
  } catch (err) {
    if (requestId !== latestRequestId) return
    notifyError(err, 'Failed to load users')
  } finally {
    if (requestId === latestRequestId) loading.value = false
  }
}

function loadRoles () {
  return store.dispatch('getRoles')
    .then(response => { roles.value = response.Data || [] })
    .catch(err => notifyError(err, 'Failed to load roles'))
}

function openRoles (user) {
  rolesModalUser.value = user
}

function openRolesFromDetail (user) {
  detailModalUser.value = null
  rolesModalUser.value = user
}

function onRolesSaved () {
  rolesModalUser.value = null
  loadUsers()
}

async function confirmDelete (user) {
  if (isCurrentUser(user)) return
  const res = await $vue?.proxy.$swal.fire({
    icon: 'warning',
    title: 'Delete user',
    html: 'This permanently deletes <strong>' + user.email + '</strong> and cannot be undone.<br>Type the email to confirm.',
    input: 'text',
    inputPlaceholder: user.email,
    inputValidator: value => (value === user.email ? undefined : 'Type the email exactly to confirm'),
    showCancelButton: true,
    confirmButtonText: 'Delete user',
    cancelButtonText: 'Cancel'
  })
  if (!res.isConfirmed) return
  try {
    await store.dispatch('deleteUser', { ID: user.id })
  } catch (err) {
    notifyError(err, 'Failed to delete ' + user.email)
    return
  }
  loadUsers()
}

let searchTimer = null
watch(searchInput, (value) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    if (value === search.value) return
    updateQuery({ search: value, page: 1 })
  }, SEARCH_DEBOUNCE_MS)
})
watch(() => route.query.search, (value) => {
  if ((value || '') !== searchInput.value) searchInput.value = value || ''
})
watch([search, roleFilter, sort, dir, page, perPage], loadUsers)

onMounted(() => {
  loadUsers()
  loadRoles()
})
</script>

<style scoped>
.roles-cell,
.user-cell {
  cursor: pointer;
}
.th-sort {
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;
}
.th-sort:hover {
  color: var(--tblr-body-color);
}
.users-table .avatar {
  flex-shrink: 0;
}

@media (max-width: 767.98px) {
  .users-table,
  .users-table tbody,
  .users-table tr,
  .users-table td {
    display: block;
    width: 100%;
  }
  .users-table thead {
    display: none;
  }
  .users-table tr {
    position: relative;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--tblr-border-color);
  }
  .users-table td {
    border: 0;
    padding: 0;
    min-width: 0;
  }
  .users-table td.user-cell {
    padding-right: 2.5rem;
  }
  .users-table td.roles-cell {
    margin-top: 0.5rem;
  }
  .users-table td.roles-cell .badge {
    margin-bottom: 0.25rem;
    white-space: normal;
    text-align: left;
  }
  .users-table td.created-cell {
    margin-top: 0.375rem;
    font-size: 0.75rem;
  }
  .users-table td.created-cell::before {
    content: 'Created ';
  }
  .users-table td.actions-cell {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: auto;
  }
  .users-table tr.state-row {
    padding: 0;
  }
  .users-table tr.state-row td {
    padding: 1.5rem 1rem;
  }
  .users-table tr.state-row td::before {
    content: none;
  }
}
</style>
